/**
 * The one walk over a form's answered elements as the filler saw them.
 *
 * Every consumer that reads answers off a version's content — the score
 * summary, the live fill stats, the output answer list — must walk through
 * here rather than recursing `elements` itself. The rules a filler's view
 * obeys live in one place, so a new element type or a new consumer cannot
 * count a question the filler never saw:
 *
 *   - a fired logic rule's hidden state wins over the static `data.hidden`
 *     flag (a `show` can reveal a statically hidden field);
 *   - a hidden container hides its whole subtree, whatever rules its
 *     children carry, because the fill never mounts it;
 *   - archived elements are gone;
 *   - a repeat scope (a section with `data.repeat`, or line items) is
 *     walked once per stored instance, with logic re-resolved against the
 *     top-level answers overlaid with that instance's own, and its leaves
 *     read their answers from the instance.
 *
 * Validation, the live fill stats and the output answer list all walk
 * through here. ISM carries the same walk in FormLogicVisibility.php; both
 * are checked against visibility.fixtures.json.
 */

import type { DocumentElement, LogicRule, ResolvedElementState } from "../types";
import { resolveLogic } from "./evaluate";

export const VISIBILITY_CONTAINER_TYPES: ReadonlySet<string> = new Set([
  "pages",
  "page",
  "tabs",
  "tab",
  "section",
  "repeatable-section",
  "columns",
]);

export type FormValues = Record<string, unknown>;

/** A container the walk passed through on the way to a leaf. */
export interface VisibleAncestor {
  element: DocumentElement;
  /** Set on a repeat scope: which stored instance the walk is inside. */
  instance?: number;
  /**
   * Set on a repeat scope: how many instances it holds, so a consumer can
   * say "2 of 3" without going back to the stored answers to count them.
   */
  instanceCount?: number;
}

export interface VisibleLeaf {
  element: DocumentElement;
  /** The values the leaf's answer lives in: the root values, or its repeat instance. */
  values: FormValues;
  /** Where the leaf sits: element ids, with the instance index inside a repeat scope. */
  path: Array<string | number>;
  /** The fired logic state for the leaf, when a rule targets it. */
  state: ResolvedElementState | undefined;
  /** The containers above the leaf, outermost first. */
  ancestors: VisibleAncestor[];
}

export interface VisibleWalkOptions {
  /**
   * Treat a repeat scope with nothing stored as one empty instance, as the
   * fill shows one by default. Validation wants this so a required field
   * inside an untouched repeat still flags; scoring and outputs do not.
   */
  emptyRepeatAsOneInstance?: boolean;
  /**
   * Called as the walk enters a visible container, in document order: once
   * for each container, and for a repeat scope once for the scope and then
   * once per instance (with `instance` set). Lets a consumer emit headings
   * without walking the tree itself.
   */
  onEnter?: (ancestor: VisibleAncestor, path: Array<string | number>) => void;
}

export interface VisibleContent {
  elements?: DocumentElement[] | undefined;
  logic?: LogicRule[] | undefined;
}

/** The fill renderer's hide decision for one element. */
export function isElementHidden(
  element: DocumentElement,
  resolved: Map<string, ResolvedElementState>,
): boolean {
  const state = resolved.get(element.id);
  if (state?.hidden !== undefined) return state.hidden;
  return (element.data as { hidden?: unknown } | undefined)?.hidden === true;
}

export function isRepeatScope(element: DocumentElement): boolean {
  if (element.typeId === "line-items") return true;
  return (
    element.typeId === "section" &&
    (element.data as { repeat?: unknown } | undefined)?.repeat === true
  );
}

/**
 * Visit every non-container element the filler could see, with the values
 * its answer should be read from.
 */
export function forEachVisibleLeaf(
  content: VisibleContent,
  values: FormValues,
  visit: (leaf: VisibleLeaf) => void,
  options: VisibleWalkOptions = {},
): void {
  const rules = Array.isArray(content.logic) && content.logic.length > 0 ? content.logic : undefined;
  const walk = (
    elements: DocumentElement[] | undefined,
    scopeValues: FormValues,
    resolved: Map<string, ResolvedElementState>,
    path: Array<string | number>,
    ancestors: VisibleAncestor[],
  ): void => {
    for (const element of elements ?? []) {
      if (!element || typeof element !== "object") continue;
      if ((element as { archived?: boolean }).archived) continue;
      if (isElementHidden(element, resolved)) continue;

      if (isRepeatScope(element)) {
        const stored = scopeValues[element.id];
        const instances: unknown[] = Array.isArray(stored)
          ? stored
          : options.emptyRepeatAsOneInstance
            ? [{}]
            : [];
        if (instances.length === 0) continue;
        const scopePath = [...path, element.id];
        options.onEnter?.({ element, instanceCount: instances.length }, scopePath);
        instances.forEach((instance, index) => {
          const instanceValues: FormValues =
            instance && typeof instance === "object" && !Array.isArray(instance)
              ? (instance as FormValues)
              : {};
          const frame: VisibleAncestor = {
            element,
            instance: index,
            instanceCount: instances.length,
          };
          const instancePath = [...scopePath, index];
          options.onEnter?.(frame, instancePath);
          walk(
            element.elements as DocumentElement[] | undefined,
            instanceValues,
            resolveLogic(rules, { ...values, ...instanceValues }),
            instancePath,
            [...ancestors, frame],
          );
        });
        continue;
      }

      if (VISIBILITY_CONTAINER_TYPES.has(element.typeId)) {
        const frame: VisibleAncestor = { element };
        const containerPath = [...path, element.id];
        options.onEnter?.(frame, containerPath);
        walk(
          element.elements as DocumentElement[] | undefined,
          scopeValues,
          resolved,
          containerPath,
          [...ancestors, frame],
        );
        continue;
      }

      visit({
        element,
        values: scopeValues,
        path: [...path, element.id],
        state: resolved.get(element.id),
        ancestors,
      });
    }
  };
  walk(content.elements, values, resolveLogic(rules, values), [], []);
}

/** The visible leaves' paths as strings ("section/0/field"), for tests and fixtures. */
export function visibleLeafPaths(
  content: VisibleContent,
  values: FormValues,
  options: VisibleWalkOptions = {},
): string[] {
  const out: string[] = [];
  forEachVisibleLeaf(content, values, (leaf) => out.push(leaf.path.join("/")), options);
  return out;
}
